import type {
  FilterConditionPluginSpecification,
} from '@bindoc-projects/filter-condition-domain-lib';
import {
  FilterConditionPluginImpl
} from '@bindoc-projects/filter-condition-domain-lib';

import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';

export const INTUITIVE_FILE_OF_TRUTH_TYPE_OF_ENTRY_CONDITION_ID =
  'intuitiveFotTypeOfEntry';
export const FilterConditionIntuitiveFileOfTruthTypeOfEntryPluginType: FilterConditionPluginSpecification<
  Omit<JsonIntuitiveFileOfTruthTypeOfEntryPluginDto, 'type'>,
  JsonIntuitiveFileOfTruthTypeOfEntryPluginDto,
  IntuitiveFileOfTruthTypeOfEntryCondition
> = {
  type: 'intuitiveFotTypeOfEntry',
  category: 'case',
  displayName: {
    de: 'Intuitive File of Truth Type of Entry',
    en: 'Intuitive File of Truth Type of Entry',
  },
  sortingIndex: 8,
  occurrence: 'single',
  newInstance(): FilterConditionIntuitiveFileOfTruthTypeOfEntryPlugin {
    return new FilterConditionIntuitiveFileOfTruthTypeOfEntryPlugin([], null);
  },
  fromCondition(
    jsonObj: IntuitiveFileOfTruthTypeOfEntryCondition,
  ): FilterConditionPluginImpl<
      Omit<JsonIntuitiveFileOfTruthTypeOfEntryPluginDto, 'type'>
    > {
    return new FilterConditionIntuitiveFileOfTruthTypeOfEntryPlugin(
        jsonObj.includes ? jsonObj.includes : null,
        jsonObj.excludes ? jsonObj.excludes : null,
      );
  },
  fromObject(
    obj: JsonIntuitiveFileOfTruthTypeOfEntryPluginDto,
  ): FilterConditionPluginImpl<
      Omit<JsonIntuitiveFileOfTruthTypeOfEntryPluginDto, 'type'>
    > {
    return new FilterConditionIntuitiveFileOfTruthTypeOfEntryPlugin(
        obj.includes ? obj.includes : null,
        obj.excludes ? obj.excludes : null,
      );
  },
};

export class FilterConditionIntuitiveFileOfTruthTypeOfEntryPlugin extends FilterConditionPluginImpl<
  Omit<JsonIntuitiveFileOfTruthTypeOfEntryPluginDto, 'type'>,
  JsonIntuitiveFileOfTruthTypeOfEntryPluginDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    Omit<JsonIntuitiveFileOfTruthTypeOfEntryPluginDto, 'type'>,
    JsonIntuitiveFileOfTruthTypeOfEntryPluginDto,
    IntuitiveFileOfTruthTypeOfEntryCondition
  > = FilterConditionIntuitiveFileOfTruthTypeOfEntryPluginType;

  constructor(
    protected includes: string[],
    protected excludes: string[],
  ) {
    super(FilterConditionIntuitiveFileOfTruthTypeOfEntryPluginType.type);
  }

  override equals(
    other: FilterConditionIntuitiveFileOfTruthTypeOfEntryPlugin,
  ): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.includes, other.includes) &&
      BdArrayUtil.equals(this.excludes, other.excludes)
    );
  }

  clone(): FilterConditionIntuitiveFileOfTruthTypeOfEntryPlugin {
    return new FilterConditionIntuitiveFileOfTruthTypeOfEntryPlugin(
      this.includes ? [...this.includes] : null,
      this.excludes ? [...this.excludes] : null,
    );
  }

  toCondition(): BdCondition | null {
    return new IntuitiveFileOfTruthTypeOfEntryCondition(
        this.includes,
        this.excludes,
      );
  }

  toJSON(): JsonIntuitiveFileOfTruthTypeOfEntryPluginDto | null {
    return {
      type: this.type,
      includes: this.includes ? this.includes : null,
      excludes: this.excludes ? this.excludes : null,
    };
  }

  toValue(): Omit<JsonIntuitiveFileOfTruthTypeOfEntryPluginDto, 'type'> {
    return {
      includes: this.includes,
      excludes: this.excludes,
    };
  }

  override valid(): boolean {
    return (
      (this.includes && this.includes.length > 0) ||
      (this.excludes && this.excludes.length > 0)
    );
  }

  validationToText(): string {
    if (!this.includes && !this.excludes) {
      return 'Mindestens eine Intuitive File of Truth Type of Entry auswählen';
    }

    if (this.includes && this.includes.length === 0) {
      return 'Mindestens eine Intuitive File of Truth Type of Entry auswählen';
    } else if (this.excludes && this.excludes.length === 0) {
      return 'Mindestens eine Intuitive File of Truth Type of Entry auswählen';
    } else {
      return '';
    }
  }

  toText(): string | null {
    if (!this.includes && !this.excludes) {
      return '';
    }

    if (this.excludes) {
      return this.excludes.length === 1
        ? 'eine Intuitive File of Truth Type of Entry'
        : `${this.excludes.length} Intuitive File of Truth Type of Entries`;
    }

    return this.includes.length === 1
      ? 'eine Intuitive File of Truth Type of Entry'
      : `${this.includes.length} Intuitive File of Truth Type of Entries`;
  }
}

class IntuitiveFileOfTruthTypeOfEntryCondition extends BdContentCondition<string> {
  constructor(includes: string[], excludes: string[]) {
    super(
      INTUITIVE_FILE_OF_TRUTH_TYPE_OF_ENTRY_CONDITION_ID,
      includes,
      excludes,
    );
  }
}

interface JsonIntuitiveFileOfTruthTypeOfEntryPluginDto {
  type: string;
  includes: string[];
  excludes: string[];
}
